import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { z } from "zod";
import { AuthLayout } from "@/features/auth/components/AuthLayout";
import { useAppForm } from "@/lib/form/form-hook";
import { createResetPasswordSchema } from "@/lib/form/schemas/auth";
import { useResetPassword } from "@/features/auth/hooks/useResetPassword";
import { ApiErrorBanner } from "@/components/ui/ApiErrorBanner";
import { CheckCircle } from "lucide-react";

const searchSchema = z.object({
  token: z.string().catch(""),
});

export const Route = createFileRoute("/reset-password")({
  validateSearch: searchSchema,
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { t } = useTranslation();
  const { token } = useSearch({ from: "/reset-password" });
  const mutation = useResetPassword();
  const schema = createResetPasswordSchema(t);
  const [success, setSuccess] = useState(false);

  const form = useAppForm({
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
    validators: {
      onBlur: schema,
    },
    canSubmitWhenInvalid: true,
    onSubmit: async ({ value }) => {
      try {
        await mutation.mutateAsync({ token, password: value.password });
        setSuccess(true);
      } catch {
        // Errors handled below via mutation.error
      }
    },
  });

  // Success state
  if (success) {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center gap-4 py-4 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success/10">
            <CheckCircle size={32} className="text-success" />
          </div>
          <h1 className="text-xl font-bold text-heading">{t("auth.resetPassword.success")}</h1>
          <Link
            to="/login"
            className="mt-4 inline-flex h-10 items-center justify-center rounded-lg bg-indigo px-6 text-sm font-semibold text-white transition-all hover:bg-indigo-bright hover:shadow-lg hover:shadow-indigo/25 active:scale-[0.98]"
          >
            {t("auth.resetPassword.backToLogin")}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      {/* Header */}
      <div className="mb-6 text-center">
        <h1 className="text-2xl font-bold text-heading">{t("auth.resetPassword.title")}</h1>
        <p className="mt-1 text-sm text-muted">{t("auth.resetPassword.subtitle")}</p>
      </div>

      {/* API error banner */}
      <ApiErrorBanner
        error={mutation.error}
        fieldLabels={{ password: t("auth.resetPassword.password") }}
        className="mb-4"
      />

      {/* Form */}
      <form.AppForm>
        <form.Form className="flex flex-col gap-4" noValidate>
          <form.AppField
            name="password"
            children={(field) => (
              <field.PasswordField
                label={t("auth.resetPassword.password")}
                placeholder={t("auth.resetPassword.passwordPlaceholder")}
                autoComplete="new-password"
              />
            )}
          />

          <form.AppField
            name="confirmPassword"
            children={(field) => (
              <field.PasswordField
                label={t("auth.resetPassword.confirmPassword")}
                placeholder={t("auth.resetPassword.confirmPasswordPlaceholder")}
                autoComplete="new-password"
              />
            )}
          />

          <form.SubmitButton
            label={t("auth.resetPassword.submit")}
            loadingLabel={t("auth.resetPassword.submitting")}
          />
        </form.Form>
      </form.AppForm>

      {/* Back to login */}
      <p className="mt-6 text-center text-sm text-muted">
        <Link
          to="/login"
          className="font-medium text-indigo hover:text-indigo-bright transition-colors"
        >
          {t("auth.resetPassword.backToLogin")}
        </Link>
      </p>
    </AuthLayout>
  );
}
